import { useQuery } from "@apollo/client"
import React from "react"
import { ErrorBoundary, FallbackProps } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import {
  COMPATIBLE_FEATURE_CATALOGS,
  FEATURE_CATALOGS,
  FEATURE_CATALOG_STANDARDS,
  PROJECT_OWNER_LICENSE_INFO
} from "../../api/QuadriApi"
import { CoordinateSystemUnit, FeatureCatalogInfo, FeatureCatalogStandard } from "../../types"
import { exceptionFromApolloError } from "../../util/apolloError"
import { versionIsSameOrHigher } from "../../util/fcVersionCompare"
import Oops from "../Oops/Oops"
import { Select } from "../Select"
import { ConfigStatus } from "./ObjectModelConfigMain"

interface QuadriProjectSettingsProps {
  existingFcId: string | null

  fcStandard?: string
  setFcStandard: (std: string) => void

  featureCatalogId?: string
  setFeatureCatalogId: (id?: string) => void

  coordinateSystemUnits?: CoordinateSystemUnit
  setCoordinateSystemUnits: (csu: CoordinateSystemUnit) => void
  disableEdit?: boolean
  disableEditCoordinateSystemUnit?: boolean
  configStatus: ConfigStatus // TODO: Is this used???
  projectStatus?: ConfigStatus
  region?: string
  quadriConfigurationUiUrl: string
  onChange: () => void
  connectProjectId: string
}

// The card for selecting feature catalogs. The top level component is responsible
// for error handling. Errors will end up inside the card and not crashing the whole
// page.
const QuadriProjectSettings = (props: QuadriProjectSettingsProps) => {
  const { t } = useTranslation("config")
  return (
    <div className="d-flex justify-content-center align-items-center vh-100 mb-3">
      <div className="col-12">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <QuadriProjectSettingsInner {...props} />
        </ErrorBoundary>
      </div>
    </div>
  )
}

const QuadriProjectSettingsInner = (props: QuadriProjectSettingsProps) => {
  const { fcStandard, setFcStandard } = props
  const { featureCatalogId, setFeatureCatalogId } = props
  const { coordinateSystemUnits, setCoordinateSystemUnits } = props
  const { disableEdit, disableEditCoordinateSystemUnit, projectStatus, existingFcId } = props
  const { t } = useTranslation("config")
  const universalCatalogLabels = ["Quadri", "QuadriIFC", "LDB for Bane Nor"]
  const { onChange } = props

  // If the project is active, then only get compatible feature catalogs
  const getOnlyCompatible = projectStatus === "Active"
  const projectOwnerLicenseInfo = useQuery(PROJECT_OWNER_LICENSE_INFO, {
    variables: { connectProjectId: props.connectProjectId, region: props.region }
  })
  const licenseType = projectOwnerLicenseInfo.data?.projectOwnerLicenseInfo?.licenseType
  const isInnovateOnly = licenseType?.trim().toLowerCase() === "innovate"

  const tags = getFcTags(props.region)

  const standardsQuery = useQuery(FEATURE_CATALOG_STANDARDS, {
    variables: isInnovateOnly ? { standardId: "Quadri.IFC", tags } : { tags }
  })

  // Use "skip" in useQuery to either get all catalogs or only compatible
  const allCatalogsQuery = useQuery(FEATURE_CATALOGS, {
    variables: { tags },
    skip: getOnlyCompatible
  })
  const compatibleQuery = useQuery(COMPATIBLE_FEATURE_CATALOGS, {
    variables: { id: existingFcId, tags },
    skip: !getOnlyCompatible
  })

  // Choose the applicable catalogs query going forward
  const catalogsQuery = getOnlyCompatible ? compatibleQuery : allCatalogsQuery

  // Get list of feature catalogs to display
  const availableCatalogs = getOnlyCompatible
    ? compatibleQuery.data?.compatibleFeatureCatalogs
    : allCatalogsQuery.data?.featureCatalogs

  // Effect to update the selected standard
  React.useEffect(() => {
    if (featureCatalogId && availableCatalogs && availableCatalogs.length > 0) {
      const catalog = availableCatalogs.find((c: any) => c.id === featureCatalogId)
      if (catalog && catalog.standard) {
        setFcStandard(catalog.standard)
      }
    }
  }, [availableCatalogs, featureCatalogId, setFcStandard])

  // Propagate GraphQL errors
  if (standardsQuery.error) {
    throw exceptionFromApolloError(standardsQuery.error)
  }

  if (catalogsQuery.error) {
    throw exceptionFromApolloError(catalogsQuery.error)
  }

  const loading = standardsQuery.loading || catalogsQuery.loading || projectOwnerLicenseInfo.loading

  const standards = standardsQuery.data ? standardsQuery.data.featureCatalogStandards : []
  const versions = availableCatalogs || []

  // fixed order for universal catalogs
  const universalCatalogs = universalCatalogLabels
    .map((label) => standards.find((e: FeatureCatalogStandard) => e.name === label))
    .filter(Boolean)
    .map((e: FeatureCatalogStandard) => ({ value: e!.id, label: e!.name }))

  // Extract and sort remaining catalogs alphabetically
  const remainingCatalogs = standards
    .filter((e: FeatureCatalogStandard) => !universalCatalogLabels.includes(e.name))
    .map((e: FeatureCatalogStandard) => ({ value: e.id, label: e.name }))
    .sort((a: { label: string }, b: { label: any }) => a.label.localeCompare(b.label))

  const allCatalogs = [...universalCatalogs, ...remainingCatalogs]

  const listOfVersions = versions
    .filter((e: FeatureCatalogInfo) => e.standard === fcStandard)
    .sort(fcSort)
    .map((fcInfo: FeatureCatalogInfo) => ({
      value: fcInfo.id,
      label: fcInfo.displayText
    }))
    .reverse()

  React.useEffect(() => {
    if (!featureCatalogId && listOfVersions.length > 0) {
      setFeatureCatalogId(listOfVersions[0]?.value)
    }
  }, [listOfVersions, featureCatalogId, setFeatureCatalogId])

  return (
    <>
      <div className="col-12 ml-1">
        <p className="text-meta display-linebreak">
          {t("quadriProjectSettings.description")}&nbsp;
          <a href={props.quadriConfigurationUiUrl} target="_blank" rel="noreferrer">
            {t("quadriProjectSettings.quadriConfiguration")}
          </a>
          .
        </p>
      </div>
      <div className="col-12 ml-1">
        <Select
          label={t("objectModelConfig.typeCatalog")}
          // The "bold" part is for the word "Extension" in the top two select components
          extensionBold=""
          value={fcStandard}
          onChange={(e: any) => {
            setFcStandard(e.target.value)
            setFeatureCatalogId(undefined)
            onChange()
          }}
          options={allCatalogs}
          disabled={disableEdit}
          required={true}
          loading={loading}
        />

        <Select
          label={t("objectModelConfig.typeCatalogVersion")}
          extensionBold=""
          value={featureCatalogId}
          onChange={(e: any) => {
            setFeatureCatalogId(e.target.value)
            onChange()
          }}
          options={listOfVersions}
          disabled={disableEdit || !fcStandard}
          required={true}
          loading={loading}
        />

        <Select
          label={t("quadriProjectSettings.crsUnits")}
          extensionBold=""
          value={coordinateSystemUnits}
          onChange={(e: any) => {
            setCoordinateSystemUnits(e.target.value)
            onChange()
          }}
          options={availableCoordinateSystemUnits.map((j: { translationKey: string; value: CoordinateSystemUnit }) => ({
            value: j.value,
            label: t(j.translationKey)
          }))}
          disabled={disableEditCoordinateSystemUnit}
          required={true}
          loading={loading}
        />
      </div>
    </>
  )
}

// Custom error fallback to make it fit in a card with proper formatting
const ErrorFallback = (props: FallbackProps) => (
  <div className="col-12 align-center">
    <Oops body={props.error.message} />
  </div>
)

const fcSort = (a: FeatureCatalogInfo, b: FeatureCatalogInfo) => (versionIsSameOrHigher(a.version, b.version) ? 1 : -1)

const availableCoordinateSystemUnits: { translationKey: string; value: CoordinateSystemUnit }[] = [
  {
    translationKey: "quadriProjectSettings.meter",
    value: "Meter"
  },
  {
    translationKey: "quadriProjectSettings.internationalFoot",
    value: "InternationalFoot"
  },
  {
    translationKey: "quadriProjectSettings.usSurveyFoot",
    value: "UsSurveyFoot"
  }
]

const getFcTags = (region?: string) => {
  return region ? `region/${region}` : undefined
}

export default QuadriProjectSettings
